/**
 * @fileoverview PermissionGate — conditionally renders children based on RBAC
 *
 * Usage:
 *   <PermissionGate permission="finance.salary.view" fallback={<SalaryMasked />}>
 *     <SalaryAmount />
 *   </PermissionGate>
 *
 *   <PermissionGate anyOf={["finance.edit", "finance.admin"]}>
 *     <EditButton />
 *   </PermissionGate>
 */

import PropTypes from "prop-types";
import { useAuth } from "context/AuthContext";

/**
 * Renders children only when the current user has the required permission(s).
 *
 * @param {{ permission?: string, anyOf?: string[], fallback?: React.ReactNode, children: React.ReactNode }} props
 */
export function PermissionGate({ permission, anyOf, fallback = null, children }) {
  const { hasPermission, hasAnyPermission } = useAuth();

  const allowed = anyOf
    ? hasAnyPermission(anyOf)
    : permission
    ? hasPermission(permission)
    : true;

  return allowed ? children : fallback;
}

PermissionGate.propTypes = {
  /** Single permission string to check */
  permission: PropTypes.string,
  /** Array of permissions — renders if user has ANY of them */
  anyOf: PropTypes.arrayOf(PropTypes.string),
  /** Fallback UI when access is denied */
  fallback: PropTypes.node,
  /** Content to render when access is granted */
  children: PropTypes.node.isRequired,
};
